import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { IGAccountsResponse, Influencer, InfluencerState, PANDetails, SetInfluencerAction, SocialAccountData, } from '../../types/influencer/influencerTypes';
import { fetchInfluencer as fetchInfluencerAPI, updateInfluencer as updateInfluencerAPI, fetchInstagramAccounts, linkInstagramAccount, fetchAccountDeletionRequests } from '../../services/api/influencer';
import { PURGE } from 'redux-persist';
import { fetchPANDetails } from '../../services/api/wallet';
import { apiConfigs } from '../../constants/apiConfigs';
import { isFresh } from '../../utils/helpers';
import { ActivePlan } from '../../types/membership/membershipTypes';

const initialState: InfluencerState = {
    influencer: null,
    influencerId: null,
    social_accounts: [],
    accountId: null,
    isConnected: false,
    connectionRequired: false,
    loading: true,
    error: null,
    igAccount: null,
    insights: null,
    pan: null,
    selectedSocialAccount: null,
    accountDeletionRequests: {
        last_fetched_at: 0,
        items: [],
        loading: false,
        error: null
    },
    active_plan: null
};

export const updateInfluencer = createAsyncThunk<{influencer: Influencer}, any, { rejectValue: string }>(
    'influencer/profile/update',
    async (inf, { rejectWithValue }) => {
        try {
            await updateInfluencerAPI(inf);
            const {influencer} = await fetchInfluencerAPI();
            return {influencer};
        } catch (error: any) {
            return rejectWithValue(error.response.data || 'Failed to update profile');
        }
    }
);

export const fetchInfluencer = createAsyncThunk<{influencer: Influencer, social_accounts: SocialAccountData[], active_plan: ActivePlan}, string, { rejectValue: string }>(
    'influencer/profile/fetch',
    async (token, { rejectWithValue }) => {
        try {
            const {influencer, social_accounts, active_plan} = await fetchInfluencerAPI(token);

            return {influencer, social_accounts, active_plan};
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to fetch influencer profile');
        }
    }
);

export const fetchIGAccounts = createAsyncThunk<IGAccountsResponse, void, { rejectValue: string }>(
    'influencer/ig-accounts',
    async (_, { rejectWithValue }) => {
        try {
            const response = await fetchInstagramAccounts();
            return response;
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to fetch Instagram accounts');
        }
    }
);

export const linkIGAccount = createAsyncThunk<boolean, string, { rejectValue: string }>(
    'influencer/link-ig-account',
    async (accountId, { rejectWithValue }) => {
        try {
            const status = await linkInstagramAccount(accountId);
            return status;
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to link Instagram account');
        }
    }
);

export const fetchPANDetailsThunk =  createAsyncThunk<PANDetails, void, { rejectValue: string }>(
    'influencer/pan-details',
    async (_, { rejectWithValue }) => {
        try {
            const details = await fetchPANDetails();
            return details;
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to fetch pan details');
        }
    }
);

export const fetchAccountDeletionRequestsThunk = createAsyncThunk<any[], {force?: boolean}, { rejectValue: string }>(
    'influencer/account-deletion-requests/fetch',
    async ({force=false}, { getState, rejectWithValue }) => {
        try {
            const staleTime = apiConfigs.FETCH_ACCOUNT_DELETION_REQUESTS.staleTime;
            const state = getState() as {influencer: InfluencerState};
            const lastFetchedAt = state.influencer.accountDeletionRequests.last_fetched_at;
            if(!force && isFresh(lastFetchedAt, staleTime)){
                return state.influencer.accountDeletionRequests.items;
            }
            const items = await fetchAccountDeletionRequests();
            return items;
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to fetch account deletion requests');
        }
    }
);

const influencerSlice = createSlice({
    name: 'influencer',
    initialState,
    reducers: {
        setInfluencer(state, action: PayloadAction<SetInfluencerAction>) {
            state.influencer = action.payload.influencer;
            state.influencerId = "me"
            if (action.payload.influencer.account) {
                state.isConnected = true
                state.accountId = action.payload.influencer.account.id
            }
        },
        setSelectedSocialAccount(state, action: PayloadAction<SocialAccountData | null>) {
            state.selectedSocialAccount = action.payload;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchInfluencer.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchInfluencer.fulfilled, (state, action: PayloadAction<{influencer: Influencer, social_accounts: SocialAccountData[], active_plan: ActivePlan}>) => {
                state.loading = false;
                const {influencer, social_accounts, active_plan} = action.payload;
                state.influencer = influencer;
                state.active_plan = active_plan;
                state.social_accounts = social_accounts;
                if(!state.selectedSocialAccount && social_accounts.length > 0) {
                    state.selectedSocialAccount = social_accounts[0];
                }
                state.influencerId = "me"
                state.isConnected = influencer.account ? true : false;
                state.connectionRequired = social_accounts.length === 0;

                state.error = null;
            })
            .addCase(fetchInfluencer.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload || 'Failed to fetch influencer data';
            })
            .addCase(updateInfluencer.pending, (state, action) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(updateInfluencer.fulfilled, (state, action: PayloadAction<{influencer: Influencer}>) => {
                state.loading = false;
                state.influencer = action.payload.influencer;
                state.influencerId = "me"
                state.isConnected = action.payload.influencer.account ? true : false;
                // state.connectionRequired = social_accounts.length === 0;

                state.error = null;
            })
            .addCase(updateInfluencer.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload || "Failed to update profile"
            })
            .addCase(fetchIGAccounts.pending, (state, action) => {
                state.loading = true;
                state.error = null;
            }).addCase(fetchIGAccounts.fulfilled, (state, action: PayloadAction<IGAccountsResponse>) => {
                state.loading = false;
                if (action.payload.status) {
                    if (action.payload.id && action.payload.result?.accounts[0]) {
                        state.accountId = action.payload.id
                        state.igAccount = action.payload.result.accounts[0]
                    } else {
                        state.isConnected = false
                        state.error = "No Instagram account linked"
                    }
                } else {
                    state.error = action.payload.error || 'Failed to fetch Instagram accounts';
                }
            }).addCase(fetchIGAccounts.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload || 'Failed to fetch Instagram accounts';
            })
            .addCase(linkIGAccount.pending, (state, action) => {
                state.loading = true;
            }).addCase(linkIGAccount.fulfilled, (state, action: PayloadAction<boolean>) => {
                state.loading = false;
                if (action.payload) {
                    state.isConnected = true
                    state.error = null;
                } else {
                    state.error = 'Failed to link Instagram account';
                }
            }).addCase(linkIGAccount.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload || 'Failed to link Instagram account';
            })
            .addCase(fetchPANDetailsThunk.fulfilled, (state, {payload}) => {
                state.pan = payload;   
            })
            .addCase(PURGE, () => {
                return initialState;
            })
            .addCase(fetchAccountDeletionRequestsThunk.pending, (state) => {
                state.accountDeletionRequests.loading = true;
                state.accountDeletionRequests.error = null;
            })
            .addCase(fetchAccountDeletionRequestsThunk.fulfilled, (state, {payload}) => {
                state.accountDeletionRequests = {
                    last_fetched_at: Date.now(),
                    items: payload,
                    loading: false,
                    error: null
                };
            })
            .addCase(fetchAccountDeletionRequestsThunk.rejected, (state, action) => {
                state.accountDeletionRequests.loading = false;
                state.accountDeletionRequests.error = action.payload || 'Failed to fetch account deletion requests';
            });
    },
});

export const { setInfluencer, setSelectedSocialAccount } = influencerSlice.actions;

export default influencerSlice.reducer;